import { readAdminPermissionSession } from '@/utils/permissionSessionIdentity';
import { isAccountAdmin } from '@/utils/accountRoles';
import { getUserDisplayName } from '@/utils/userDisplayName';
import { AvatarDropdown, AvatarName } from '@/components/RightContent/AvatarDropdown';
import PageLoading from '@/components/PageLoading';
import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { formatMessage, history, Link, SelectLang as UmiSelectLang } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import './styles/disable-swipe-navigation.less';
import { App, Result } from 'antd';
import { RequestConfig, RequestOptions } from '@umijs/max';
import { currentUser } from '@/services/user';
import { API_CONFIG } from '@/config/api';
import { authManager, AuthSystem } from '@/services/auth/authManager';
import { initializeConsoleCleanup } from '@/utils/consoleFilter';
import { DynamicPermissionProvider } from '@/components/PermissionProvider/DynamicPermissionProvider';
import { AuthSessionMonitor } from '@/components/AuthSessionMonitor';
import LayoutPermissionGuard from '@/components/PermissionGuard/LayoutPermissionGuard';
import { filterMenuByPermissions } from '@/utils/menuPermissionFilter.production';
import { permissionStorageService } from '@/services/permission/storageService';
import { disableWebSocketInProduction } from '@/utils/websocketDisabler';
import { initDisableSwipeNavigation } from '@/utils/disableSwipeNavigation';
import PermissionAwareMenu from '@/components/Menu/PermissionAwareMenu';
import PageRecoveryActions from '@/components/PageRecoveryActions';
import { getCurrentUserPermissions } from '@/services/permission/api';
import routes from '../config/routes';
import { rememberLastVisitedRoute } from '@/utils/lastVisitedRoute';
import { ensureLocaleLoaded } from '@/services/i18n/localeLoader';
import { WorkBadge } from '@/components/WorkItems/WorkBadge';
import { WorkItemsController } from '@/components/WorkItems/WorkItemsController';
import { WorkItemsEntry } from '@/components/WorkItems/WorkItemsEntry';
import { groupProductManagementMenu } from '@/utils/productManagementMenu';
import { groupProcurementMenu } from '@/utils/procurementMenu';
const NavigationHub = React.lazy(() => import('@/components/NavigationHub'));
const Notifications = React.lazy(() => import('@/components/RightContent/NotificationBell'));
const OfflineIndicator = React.lazy(() =>
  import('@/components/OfflineIndicator').then((module) => ({
    default: module.OfflineIndicator,
  })),
);
const PermissionChangeNotifier = React.lazy(() =>
  import('@/components/Permission/PermissionChangeNotifier').then((module) => ({
    default: module.PermissionChangeNotifier,
  })),
);
const SelectLang = () => <UmiSelectLang style={{ padding: 4 }} />;
const SettingDrawer = React.lazy(() =>
  import('@ant-design/pro-components').then((module) => ({ default: module.SettingDrawer })),
);
const REQUEST_DEBUG_FLAG = '__api_request_resolution__';
const RESPONSE_HTML_DEBUG_FLAG = '__api_html_response__';
const DEFAULT_REQUEST_TIMEOUT_MS = 30000;
const isDev = process.env.NODE_ENV === 'development';

const logRequestResolution = (details: {
  originalUrl: string;
  resolvedUrl: string;
  baseURL: string;
}) => {
  if (!isDev || typeof window === 'undefined') {
    return;
  }

  try {
    const lastLog = (window as any)[REQUEST_DEBUG_FLAG];
    const signature = JSON.stringify(details);
    if (lastLog !== signature) {
      // eslint-disable-next-line no-console
      console.log('[API_REQUEST] URL resolved', {
        ...details,
        timestamp: new Date().toISOString(),
      });
      (window as any)[REQUEST_DEBUG_FLAG] = signature;
    }
  } catch {
    // Ignore logging failures
  }
};

const logHtmlResponse = (info: {
  url?: string;
  status?: number;
  contentType?: string;
  preview?: string;
}) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const lastLog = (window as any)[RESPONSE_HTML_DEBUG_FLAG];
    const signature = JSON.stringify(info);
    if (lastLog !== signature) {
      // eslint-disable-next-line no-console
      console.error('[API_RESPONSE] Server returned HTML instead of JSON', {
        ...info,
        timestamp: new Date().toISOString(),
      });
      (window as any)[RESPONSE_HTML_DEBUG_FLAG] = signature;
    }
  } catch {
    // Ignore logging failures
  }
};

// Initialize console cleanup and error handlers
initializeConsoleCleanup();

// Disable WebSocket in production until backend is ready
disableWebSocketInProduction();

// Disable swipe back navigation
initDisableSwipeNavigation();

// Note: Permission store initialization moved to React components to prevent
// initialization outside React lifecycle that can cause infinite loops

const loginPath = '/user/login';

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: any;
  permissions?: string[];
  loading?: boolean;
  permissionsLoaded?: boolean;
  workItemsAvailableFor?: string;
  fetchUserInfo?: () => Promise<any | undefined>;
}> {
  // Locale loading starts independently from authentication so only the selected
  // language is downloaded, while user and permission requests can run in parallel.
  const localeReady = ensureLocaleLoaded();

  const fetchUserInfo = async () => {
    let requestedAdminToken: string | null = null;
    try {
      // Check if this is an MI user or admin user
      const miToken = localStorage.getItem('mi_token');
      requestedAdminToken = localStorage.getItem('token');

      // If only MI token exists, don't try to fetch admin user info
      if (miToken && !requestedAdminToken) {
        return undefined;
      }

      if (!requestedAdminToken) {
        return undefined;
      }

      const userData = await currentUser();
      if (!authManager.isCurrentToken(AuthSystem.ADMIN, requestedAdminToken)) {
        return undefined;
      }
      // Add backward compatibility fields
      if (userData) {
        userData.name = getUserDisplayName(userData);
        userData.userid = userData.id?.toString();
      }
      return userData;
    } catch (error: any) {
      console.error('Failed to fetch user info:', error);

      // If 401 error, clear token
      if (
        error?.response?.status === 401 &&
        authManager.isCurrentToken(AuthSystem.ADMIN, requestedAdminToken)
      ) {
        authManager.clearAuth(AuthSystem.ADMIN);
      }

      return undefined;
    }
  };

  // If in login page, MI routes, or franchise routes, do not fetch user info
  const { location } = history;
  const isMiRoute = location.pathname.startsWith('/mi/');
  const isFranchiseRoute = location.pathname.startsWith('/franchise/');
  const isLoginPage = location.pathname === '/user/login';

  if (!isLoginPage && !isMiRoute && !isFranchiseRoute) {
    const hasAdminToken = Boolean(localStorage.getItem('token'));
    const [currentUserResult, permissionResult] = await Promise.allSettled([
      fetchUserInfo(),
      hasAdminToken ? getCurrentUserPermissions() : Promise.resolve(null),
    ]);
    await localeReady;
    const currentUser =
      currentUserResult.status === 'fulfilled' ? currentUserResult.value : undefined;

    // Only try to load permissions from cache for initial render
    // The DynamicPermissionProvider will handle the actual loading
    let permissions: string[] = [];
    let permissionsLoaded = false;

    if (currentUser) {
      if (permissionResult.status === 'fulfilled' && permissionResult.value) {
        const permissionData = permissionResult.value;
        permissions = Array.from(new Set((permissionData.permissions || []).map((p) => p.code)));
        permissionsLoaded = true;
        console.log('[getInitialState] Loaded permissions from API:', permissions.length);
      } else {
        const permissionError =
          permissionResult.status === 'rejected'
            ? permissionResult.reason
            : new Error('Permission request was skipped');
        console.error('[getInitialState] Failed to load permissions from API:', permissionError);

        try {
          const tempData = permissionStorageService.loadTemporaryPermissions();
          if (tempData && tempData.userId === currentUser.id?.toString()) {
            permissions = tempData.userPermissions.map((p) => p.code);
            permissionsLoaded = true;
            console.log(
              '[getInitialState] Loaded temporary permissions as fallback:',
              permissions.length,
            );
          }
        } catch (fallbackError) {
          console.error('[getInitialState] Failed to load fallback permissions:', fallbackError);
        }
      }
    }

    if (typeof window !== 'undefined') {
      (window as any).__FG_INSYS_CURRENT_USER__ = currentUser ?? null;
      (window as any).__FG_INSYS_PERMISSIONS__ = permissions;
    }

    // Return initial state with permissions (if available from cache)
    return {
      fetchUserInfo,
      currentUser,
      permissions,
      loading: false,
      permissionsLoaded,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  if (typeof window !== 'undefined') {
    (window as any).__FG_INSYS_CURRENT_USER__ = null;
    (window as any).__FG_INSYS_PERMISSIONS__ = [];
  }

  await localeReady;

  return {
    fetchUserInfo,
    loading: false,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// Import access function to manually check permissions
import accessFactory from './access';

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [
      ...(initialState?.currentUser?.id &&
      initialState.workItemsAvailableFor === String(initialState.currentUser.id)
        ? [<WorkItemsEntry key="WorkItems" />]
        : []),
      <React.Suspense fallback={null} key="NavigationHub">
        <NavigationHub />
      </React.Suspense>,
      <React.Suspense fallback={null} key="Notifications">
        <Notifications />
      </React.Suspense>,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    menuItemRender: (item, dom) => {
      const selected = Boolean(
        item.path &&
          (item.path === history.location.pathname ||
            history.location.pathname.startsWith(`${item.path}/`)),
      );
      const content = (
        <WorkBadge path={item.path} selected={selected}>
          {dom}
        </WorkBadge>
      );
      if (!item.path) return content;
      if (/^https?:\/\//i.test(item.path)) return <a href={item.path}>{content}</a>;
      return <Link to={item.path}>{content}</Link>;
    },
    subMenuItemRender: (item, dom) => (
      <WorkBadge
        path={item.path}
        selected={Boolean(
          item.path &&
            (item.path === history.location.pathname ||
              history.location.pathname.startsWith(`${item.path}/`)),
        )}
      >
        {dom}
      </WorkBadge>
    ),
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    // footerRender: () => <Footer />,
    footerRender: false,
    // Filter menu items based on permissions
    menuDataRender: (menuData) => {
      // Only render menu items if user is logged in
      if (!initialState?.currentUser) {
        if (isDev) console.debug('[Menu] No user, returning empty menu');
        return [];
      }

      const session = readAdminPermissionSession();
      if (session && session.userId !== String(initialState.currentUser.id)) return [];

      // Try to get permissions from multiple sources
      let permissions = initialState?.permissions || [];

      // If permissions not in initial state, try permission store
      if (permissions.length === 0) {
        const { usePermissionStore } = require('@/stores/permissionStore');
        const permissionStore = usePermissionStore.getState();
        if (
          permissionStore.isInitialized &&
          permissionStore.userId === String(initialState.currentUser.id) &&
          permissionStore.userPermissions.length > 0
        ) {
          permissions = permissionStore.getUserPermissions();
          if (isDev) console.debug('[Menu] Got permissions from store:', permissions.length);
        }
      }

      // If still no permissions, try temporary data as last resort (only for page refresh)
      if (permissions.length === 0) {
        try {
          const tempData = permissionStorageService.loadTemporaryPermissions();
          if (tempData && tempData.userId === initialState.currentUser.id?.toString()) {
            permissions = tempData.userPermissions.map((p) => p.code);
            if (isDev) {
              console.debug(
                '[Menu] Got permissions from temporary storage (page refresh):',
                permissions.length,
              );
            }
          }
        } catch (error) {
          console.error('[Menu] Failed to load temporary permissions:', error);
        }
      }

      // Special case: if user is admin, ensure they have all permissions
      if (isAccountAdmin(initialState.currentUser)) {
        if (isDev) console.log('[Menu] Admin user detected, granting full menu access');
        // For admin, we'll return the full menu without filtering
        return groupProcurementMenu(
          groupProductManagementMenu(menuData, (key) =>
            formatMessage({ id: `menu.product-management.group-${key}` }),
          ),
          (key) => formatMessage({ id: `menu.purchase-order-management.group-${key}` }),
          formatMessage({ id: 'menu.purchase-order-management' }),
        );
      }

      // If still no permissions, show minimal menu for navigation
      if (permissions.length === 0) {
        console.warn('[Menu] No permissions found, showing minimal menu');
        // Return at least the user management menu for admins to fix permissions
        return menuData.filter(
          (item) => item.path === '/user-management' || item.path === '/role-management',
        );
      }

      if (isDev) {
        console.log('[Menu] Total permissions:', permissions.length);
        console.log('[Menu] Sample permissions:', permissions.slice(0, 10));
      }

      // Generate access control for current state
      const currentAccess = accessFactory({
        ...initialState,
        permissions,
      });

      // Use production-ready menu filtering
      const filteredMenu = filterMenuByPermissions(menuData, currentAccess, permissions);

      if (isDev) {
        console.debug('[Menu] Filtered menu items:', filteredMenu.length);
      }
      return groupProcurementMenu(
        groupProductManagementMenu(filteredMenu, (key) =>
          formatMessage({ id: `menu.product-management.group-${key}` }),
        ),
        (key) => formatMessage({ id: `menu.purchase-order-management.group-${key}` }),
        formatMessage({ id: 'menu.purchase-order-management' }),
      );
    },
    onPageChange: () => {
      const { location } = history;
      const isMiRoute = location.pathname.startsWith('/mi/');
      const isFranchiseRoute = location.pathname.startsWith('/franchise/');

      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('[onPageChange] Path:', location.pathname);
        console.log('[onPageChange] Has user:', !!initialState?.currentUser);
        console.log('[onPageChange] Permissions loaded:', initialState?.permissionsLoaded);
      }

      // Skip redirect for MI routes and franchise routes as they have their own auth
      if (isMiRoute || isFranchiseRoute) {
        return;
      }

      if (initialState?.currentUser) {
        const currentAccess = accessFactory({
          currentUser: initialState.currentUser,
          permissions: initialState.permissions || [],
        });
        const currentUserId =
          initialState.currentUser.id ?? initialState.currentUser.userid ?? undefined;

        rememberLastVisitedRoute(
          currentUserId,
          `${location.pathname}${location.search || ''}${location.hash || ''}`,
          currentAccess,
          routes,
        );
      }

      // Prevent redirect loops
      const redirectKey = 'page_change_redirect';
      const lastRedirect = sessionStorage.getItem(redirectKey);
      const now = Date.now();

      // If we redirected in the last 2 seconds, don't redirect again
      if (lastRedirect && now - parseInt(lastRedirect) < 2000) {
        console.warn('[onPageChange] Preventing redirect loop');
        return;
      }

      // 如果沒有載入使用者但已存在 admin token，先等待使用者資訊載入，避免誤導向登入頁
      const hasAdminToken = authManager.isAuthenticated(AuthSystem.ADMIN);
      // 僅在沒有 token 且沒有使用者時才導向登入頁
      if (!hasAdminToken && !initialState?.currentUser && location.pathname !== loginPath) {
        sessionStorage.setItem(redirectKey, now.toString());
        const returnTo = `${location.pathname}${location.search || ''}${location.hash || ''}`;
        history.push(`${loginPath}?redirect=${encodeURIComponent(returnTo)}`);
      }
    },
    bgLayoutImgList: [],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    unAccessible: (
      <Result
        status="403"
        title="403"
        subTitle="抱歉，您沒有權限訪問此頁面。"
        extra={<PageRecoveryActions />}
      />
    ),
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      if (initialState?.loading) return <PageLoading />;
      return (
        <App>
          <DynamicPermissionProvider>
            <LayoutPermissionGuard>
              <AuthSessionMonitor />
              <WorkItemsController
                userId={initialState?.currentUser?.id}
                permissionCodes={initialState?.permissions}
              />
              <React.Suspense fallback={null}>
                <PermissionChangeNotifier />
                <OfflineIndicator />
              </React.Suspense>
              {process.env.NODE_ENV === 'development' && (
                <React.Suspense fallback={null}>
                  {React.createElement(
                    React.lazy(() =>
                      import('@/components/PermissionDebugPanel').then((module) => ({
                        default: module.PermissionDebugPanel,
                      })),
                    ),
                  )}
                </React.Suspense>
              )}
              <PermissionAwareMenu>
                {children}
                {isDev && (
                  <React.Suspense fallback={null}>
                    <SettingDrawer
                      disableUrlParams
                      enableDarkTheme
                      settings={initialState?.settings}
                      onSettingChange={(settings) => {
                        setInitialState((preInitialState) => ({
                          ...preInitialState,
                          settings,
                        }));
                      }}
                    />
                  </React.Suspense>
                )}
              </PermissionAwareMenu>
            </LayoutPermissionGuard>
          </DynamicPermissionProvider>
        </App>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
  requestInterceptors: [
    (config: RequestOptions) => {
      const url = config.url || '';
      const isAbsoluteUrl = /^https?:\/\//i.test(url);

      if (isAbsoluteUrl) {
        logRequestResolution({
          originalUrl: url,
          resolvedUrl: url,
          baseURL: '[absolute]',
        });
        return config;
      }

      const baseURL = API_CONFIG.getBaseURL();

      if (!baseURL) {
        return config;
      }

      const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
      const resolvedUrl = `${baseURL}${normalizedUrl}`;

      logRequestResolution({
        originalUrl: url,
        resolvedUrl,
        baseURL,
      });

      return {
        ...config,
        url: resolvedUrl,
      };
    },
    (config: RequestOptions) => {
      const url = config.url || '';

      // Use authManager to get appropriate headers
      const authHeaders = authManager.getAuthHeaders(url);

      // Debug logging removed for production

      // Attach auth headers to request
      const resolvedTimeout =
        typeof config.timeout === 'number' ? config.timeout : DEFAULT_REQUEST_TIMEOUT_MS;

      return {
        ...config,
        timeout: resolvedTimeout,
        headers: {
          ...config.headers,
          ...authHeaders,
        },
        // Enable credentials for cross-origin requests
        withCredentials: true,
      };
    },
  ],
  responseInterceptors: [
    async (response) => {
      // Check if response is HTML instead of JSON
      const contentType = response.headers['content-type'] || '';

      if (contentType.includes('text/html') && response.config?.url?.includes('/api/')) {
        const statusCode = response.status || 403;
        const url = response.config?.url;
        const preview =
          typeof response.data === 'string' ? response.data.slice(0, 200) : '[non-string body]';

        logHtmlResponse({
          url,
          status: statusCode,
          contentType,
          preview,
        });

        // Construct a proper error response
        const errorResponse = {
          ...response,
          data: {
            success: false,
            errorCode: statusCode,
            errorMessage: `Server returned HTML instead of JSON. Status: ${statusCode}`,
            showType: 2, // ERROR_MESSAGE
          },
        };

        // If it's a 403 and we're trying to access permissions, it might be auth issue
        if (statusCode === 403 && response.config?.url?.includes('/auth/permissions')) {
          console.error('Permission fetch returned 403. Token might be invalid or expired.');
          // Don't redirect here, let the error handler deal with it
        }

        return errorResponse as typeof response;
      }

      // For normal responses, pass through
      return response;
    },
    // Include existing response interceptors from errorConfig
    ...(errorConfig.responseInterceptors || []),
  ],
};
